import { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Comment from "./Comment";
import CommentForm from "./CommentForm";

const UNSAVED_COMMENT_WARNING =
  "You have an unsaved comment. If you leave now, your comment text will be lost.";

const Comments = ({
  currentUser,
  comments = [],
  updateSubmittal,
  updateReview,
  updateMarkup,
  onUnsavedDraftChange,
}) => {
  const [backendComments, setBackendComments] = useState([]);
  const [activeComment, setActiveComment] = useState(null);
  const [draftByKey, setDraftByKey] = useState({});
  const [showUnsavedCommentModal, setShowUnsavedCommentModal] =
    useState(false);
  const [pendingUnsavedCommentAction, setPendingUnsavedCommentAction] =
    useState(null);
  const bypassInterceptionRef = useRef(false);
  const onUnsavedDraftChangeRef = useRef(onUnsavedDraftChange);

  useEffect(() => {
    onUnsavedDraftChangeRef.current = onUnsavedDraftChange;
  }, [onUnsavedDraftChange]);

  const hasUnsavedDraft = Object.values(draftByKey).some(
    (value) => String(value || "").trim().length > 0,
  );

  useEffect(() => {
    if (typeof onUnsavedDraftChangeRef.current === "function") {
      onUnsavedDraftChangeRef.current(hasUnsavedDraft);
    }
  }, [hasUnsavedDraft]);

  useEffect(() => {
    return () => {
      if (typeof onUnsavedDraftChangeRef.current === "function") {
        onUnsavedDraftChangeRef.current(false);
      }
    };
  }, []);

  useEffect(() => {
    if (comments?.length > 0) {
      setBackendComments(comments);
    }
  }, [comments]);

  const handleDraftChange = (draftKey, text) => {
    const normalizedKey = String(draftKey || "new");
    const normalizedText = String(text || "");

    setDraftByKey((prev) => {
      const next = { ...prev };
      if (normalizedText.trim().length > 0) {
        next[normalizedKey] = normalizedText;
      } else {
        delete next[normalizedKey];
      }
      return next;
    });
  };

  const requestUnsavedCommentConfirmation = (action) => {
    setPendingUnsavedCommentAction(action || null);
    setShowUnsavedCommentModal(true);
  };

  const closeUnsavedCommentModal = () => {
    setShowUnsavedCommentModal(false);
    setPendingUnsavedCommentAction(null);
  };

  const confirmUnsavedCommentLeave = () => {
    const action = pendingUnsavedCommentAction;
    closeUnsavedCommentModal();

    if (!action) {
      return;
    }

    bypassInterceptionRef.current = true;

    if (action.type === "link" && action.href) {
      window.location.assign(action.href);
      return;
    }

    if (action.type === "modal-close" && action.element?.click) {
      action.element.click();
    } else if (action.type === "modal-escape") {
      const modalElement = action.modalElement;
      const closeButton = modalElement?.querySelector?.(".btn-close");
      if (closeButton?.click) {
        closeButton.click();
      }
    }

    window.setTimeout(() => {
      bypassInterceptionRef.current = false;
    }, 0);
  };

  useEffect(() => {
    if (!hasUnsavedDraft) {
      return undefined;
    }

    const onBeforeUnload = (event) => {
      // Browsers only allow native beforeunload prompts for tab/window close.
      event.preventDefault();
      event.returnValue = UNSAVED_COMMENT_WARNING;
      return UNSAVED_COMMENT_WARNING;
    };

    const isWithinCommentsWidget = (element) => {
      return Boolean(element?.closest?.(".comments"));
    };

    const isLikelyModalCloseAction = (eventTarget) => {
      const element = eventTarget?.closest?.(
        "button, a, [data-bs-dismiss='modal'], .btn-close",
      );
      if (!element) {
        return false;
      }

      if (!element.closest?.(".modal")) {
        return false;
      }

      if (element.matches?.(".btn-close, [data-bs-dismiss='modal']")) {
        return true;
      }

      if (isWithinCommentsWidget(element)) {
        return false;
      }

      const label = String(element.textContent || "")
        .trim()
        .toLowerCase();

      if (!label) {
        return false;
      }

      const isCloseLabel = label === "close" || label === "cancel";
      if (!isCloseLabel) {
        return false;
      }

      const className = String(element.className || "").toLowerCase();
      return (
        className.includes("action-btn") ||
        className.includes("markup-detail-modal") ||
        className.includes("project-status-modal")
      );
    };

    const getBackdropClickElement = (eventTarget) => {
      const target = eventTarget;
      if (!target || !(target instanceof Element)) {
        return null;
      }

      // React-Bootstrap backdrop dismiss commonly happens when clicking the
      // modal root outside `.modal-dialog`.
      const modalRoot = target.closest(".modal.show");
      if (
        modalRoot &&
        target === modalRoot &&
        !target.closest?.(".modal-dialog")
      ) {
        return target;
      }

      // Fallback for direct backdrop element clicks.
      if (target.classList?.contains("modal-backdrop")) {
        return target;
      }

      return null;
    };

    // Catch common in-app navigation via link clicks.
    const onDocumentClickCapture = (event) => {
      if (bypassInterceptionRef.current) {
        return;
      }

      const backdropElement = getBackdropClickElement(event.target);
      if (backdropElement) {
        requestUnsavedCommentConfirmation({
          type: "modal-close",
          element: backdropElement,
        });
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        return;
      }

      const linkElement = event.target?.closest?.("a[href]");
      if (!linkElement) {
        if (!isLikelyModalCloseAction(event.target)) {
          return;
        }

        const triggerElement = event.target?.closest?.(
          "button, a, [data-bs-dismiss='modal'], .btn-close",
        );
        requestUnsavedCommentConfirmation({
          type: "modal-close",
          element: triggerElement || null,
        });
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        return;
      }

      const href = String(linkElement.getAttribute("href") || "").trim();
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) {
        return;
      }

      if (linkElement.target && linkElement.target !== "_self") {
        return;
      }

      requestUnsavedCommentConfirmation({
        type: "link",
        href,
      });
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    const onDocumentKeyDownCapture = (event) => {
      if (bypassInterceptionRef.current) {
        return;
      }

      if (event.key !== "Escape") {
        return;
      }

      const modalElement = event.target?.closest?.(".modal");
      if (!modalElement) {
        return;
      }

      requestUnsavedCommentConfirmation({
        type: "modal-escape",
        modalElement,
      });
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onDocumentClickCapture, true);
    document.addEventListener("keydown", onDocumentKeyDownCapture, true);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onDocumentClickCapture, true);
      document.removeEventListener("keydown", onDocumentKeyDownCapture, true);
    };
  }, [hasUnsavedDraft]);

  const saveComment = (text) => {
    const tempComment = {
      id: backendComments.length + 1,
      user: currentUser,
      comment: text,
      date: new Date().toISOString(),
    };
    const tempComments = [...backendComments, tempComment];
    setBackendComments(tempComments);
    updateReview && updateReview(tempComments);
    updateSubmittal && updateSubmittal(tempComments);
    updateMarkup && updateMarkup(tempComments);
  };

  const deleteComment = (commentId) => {
    if (window.confirm("Are you sure you would like to delete this comment?")) {
      const tempComments = backendComments.filter(
        (comment) => comment.id !== commentId,
      );
      setBackendComments(tempComments);
      updateReview && updateReview(tempComments);
      updateSubmittal && updateSubmittal(tempComments);
      updateMarkup && updateMarkup(tempComments);
    }
  };

  const updateComment = (text, commentId) => {
    const tempComments = backendComments.map((comment) => {
      if (comment.id === commentId) {
        return {
          ...comment,
          comment: text,
        };
      }
      return comment;
    });
    setBackendComments(tempComments);
    updateReview && updateReview(tempComments);
    updateSubmittal && updateSubmittal(tempComments);
    updateMarkup && updateMarkup(tempComments);
    setActiveComment(null);
  };

  return (
    <>
      <div className="comments">
        <div style={{ height: "169px", overflowY: "scroll" }}>
          {backendComments.length > 0 &&
            backendComments?.map((comment) => (
              <Comment
                key={comment?.id}
                comment={comment}
                currentUser={currentUser}
                deleteComment={deleteComment}
                activeComment={activeComment}
                setActiveComment={setActiveComment}
                updateComment={updateComment}
                onDraftChange={handleDraftChange}
              />
            ))}
        </div>
        <div>
          <CommentForm
            submitLabel="Submit"
            draftKey="new"
            onDraftChange={handleDraftChange}
            handleSubmit={saveComment}
          />
        </div>
      </div>

      <Modal
        show={showUnsavedCommentModal}
        onHide={closeUnsavedCommentModal}
        dialogClassName="project-info-modal save-markup-modal"
        centered
      >
        <Modal.Header className="project-info-modal__header save-markup-modal__header">
          <Modal.Title>Unsaved Comment</Modal.Title>
          <div
            data-bs-theme="dark"
            style={{
              position: "absolute",
              right: 20,
            }}
          >
            <button
              type="button"
              className="btn-close project-info-modal__close"
              onClick={closeUnsavedCommentModal}
              aria-label="Close"
            />
          </div>
        </Modal.Header>
        <Modal.Body className="project-info-modal__body save-markup-modal__body">
          <p style={{ marginBottom: "0.75rem" }}>{UNSAVED_COMMENT_WARNING}</p>
          <div
            className="save-markup-modal__actions"
            style={{ display: "flex", gap: "8px" }}
          >
            <button
              type="button"
              className="btn btn-outline-danger project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={confirmUnsavedCommentLeave}
            >
              Leave
            </button>
            <button
              type="button"
              className="btn btn-outline-success project-status-modal__action-btn save-markup-modal__action-btn"
              onClick={closeUnsavedCommentModal}
            >
              Stay
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Comments;
